import cx from 'classnames'
import React from 'react'
import { CSSTransition } from 'react-transition-group'

import AppTemplate from '@cinch/oxd-components/app-template/src'
import Footer from '@cinch/oxd-components/footer/src'
import HeaderIconComponent, { Theme } from '@cinch/oxd-components/header-icon/src'
import { Search as SearchIcon } from '@cinch/oxd-components/icons/src'
import { Brand } from '@cinch/oxd-components/icons/src'
import Link from '@cinch/oxd-components/link/src'
import MobileNavigation from '@cinch/oxd-components/mobile-navigation/src'
import SiteNavigation from '@cinch/oxd-components/navigation/src'
import SVG from '@cinch/oxd-components/svg/src'
import Wrapper from '@cinch/oxd-components/wrapper/src'
import CompareBar from '@cinch/oxd-containers/compare-bar/src'
import GlobalSearch from '@cinch/oxd-containers/global-search/src'
import UserAuthModal from '@cinch/oxd-containers/user-auth-modal/src'
import UserNavigation from '@cinch/oxd-containers/user-navigation/src'
import { isIOS } from '@cinch/oxd-utils/browser-support/src'
import { focusSearchInput } from '@cinch/oxd-utils/form/src'
import useBodyReset from '@cinch/oxd-hooks/body-reset/src'

import * as styles from './regular.module.css'

export interface RegularLayoutProps {
  defaultSearch?: string
  isFluid?: boolean
  isSlim?: boolean
  hasBleed?: boolean
  isFullHeightHero?: boolean
  hero?: React.ReactNode
  dimMenu?: boolean
  hasCompareBar?: boolean
  searchIsOpen: boolean
  toggleSearch: (isOpen: boolean) => any
}

const logoWidth = 75

const RegularLayoutComponent: React.FC<RegularLayoutProps> = ({
  children,
  isFluid,
  isSlim,
  hasBleed,
  isFullHeightHero,
  hero,
  dimMenu,
  hasCompareBar,
  searchIsOpen,
  toggleSearch,
}) => {
  useBodyReset()

  return (
    <AppTemplate>
      <div className={styles.layout}>
        <header
          className={cx(styles.hero, {
            [styles.fullHeightHero]: isFullHeightHero,
            [styles.headerHasBleed]: hasBleed,
          })}
        >
          <Wrapper isFullHeight={!hero}>
            <div className={styles.header}>
              <Link to="/" title="cinch">
                <SVG className={styles.brand} height="30px" width={`${logoWidth}px`} children={Brand} />
              </Link>
              <CSSTransition
                in={!searchIsOpen}
                unmountOnExit={true}
                timeout={0}
                classNames={{
                  enter: styles.menuItemsEnter,
                  enterActive: styles.menuItemsEnterActive,
                  exit: styles.menuItemsExit,
                  exitActive: styles.menuItemsExitActive,
                  exitDone: styles.menuItemsExitDone,
                }}
              >
                <>
                  <div className={styles.desktopHeader}>
                    <SiteNavigation dimmable={dimMenu} />
                  </div>
                  <div className={cx(styles.row, { [styles.dimmedMenu]: dimMenu })}>
                    <HeaderIconComponent
                      svg={SearchIcon}
                      onClick={() => {
                        toggleSearch(true)
                        if (isIOS()) {
                          focusSearchInput()
                        }
                      }}
                      label="Open Search"
                      theme={Theme.dark}
                    />
                    <UserNavigation theme={Theme.dark} />
                    <div className={styles.mobileHeader}>
                      <MobileNavigation />
                    </div>
                  </div>
                </>
              </CSSTransition>
            </div>
          </Wrapper>
          {hero && <Wrapper isFullHeight={isFullHeightHero}>{hero}</Wrapper>}
        </header>

        <div
          className={cx(styles.page, {
            [styles.fullHeightHero]: isFullHeightHero,
            [styles.pageHasBleed]: hasBleed,
          })}
        >
          <Wrapper
            hasOverflow={!isFullHeightHero}
            isFullHeight={!isFullHeightHero}
            isFluid={isFluid}
            isSlim={isSlim}
            children={children}
          />
          <div
            className={cx(styles.footerWrapper, {
              [styles.hasChildren]: !!children,
            })}
          >
            <Wrapper isFluid={false}>
              <Footer />
            </Wrapper>
          </div>
        </div>
      </div>

      <GlobalSearch>
        {(searchnode) => <Wrapper children={<div className={styles.searchbarContainer}>{searchnode}</div>} />}
      </GlobalSearch>
      <UserAuthModal />

      {hasCompareBar && <CompareBar />}
    </AppTemplate>
  )
}
export default RegularLayoutComponent
